import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Skeleton } from 'primereact/skeleton';

import Layout from '../Layout/Layout';
import {
  useAutomationSuggestions, useAutomationSettings, useApproveSuggestion,
  useDismissSuggestion, useScanNow, useUpdateAutomationSettings,
} from '../../queries/automation';
import './automation-feed.css';
import SubTabs from '../../nav/SubTabs';
import { useInHub } from '../../nav/useInHub';

const TRIGGERS = ['reward_unlocked', 'near_reward', 'win_back', 'birthday_soon', 'spin_expiring', 'milestone'];

/**
 * Customer 360 — Phase 2. The owner "Today" feed: the system watches each guest
 * and suggests the next move; nothing sends until the owner approves. Cards show
 * the suggestion + a one-tap action (send email / celebrate / mark done) and a
 * dismiss. A settings dialog sets each trigger to Off / Suggest / Auto and the
 * TV-celebration opt-in. Honey & Ink, bilingual EN/AR.
 */
export default function AutomationFeed() {
  const { t, i18n } = useTranslation();
  const inHub = useInHub();
  const isAr = (i18n.language?.split('-')[0] || 'en') === 'ar';

  const [tab, setTab] = useState('pending');
  const { data, isLoading } = useAutomationSuggestions(tab);
  const approve = useApproveSuggestion();
  const dismiss = useDismissSuggestion();
  const scan = useScanNow();
  const [busyId, setBusyId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const suggestions = data?.suggestions ?? [];
  const counts = data?.counts ?? {};

  const act = (fn, id) => { setBusyId(id); fn.mutate(id, { onSettled: () => setBusyId(null) }); };

  return (
    <Layout>
      <div className="auto360">
        <div className="a-head">
          {!inHub && <div className="a-head-ic"><i className="pi pi-sparkles" /></div>}
          {!inHub && (
            <div>
              <div className="a-title">{t('automation.title')}</div>
              <div className="a-sub">{t('automation.subtitle')}</div>
            </div>
          )}
          <div className="a-actions">
            <Button label={t('automation.scan_now')} icon="pi pi-refresh" outlined loading={scan.isLoading}
              onClick={() => scan.mutate()} />
            <Button icon="pi pi-cog" rounded text aria-label={t('automation.settings')} onClick={() => setSettingsOpen(true)} />
          </div>
        </div>

        <div className="a-onboard"><span>💡</span><span>{t('automation.onboard')}</span></div>

        {inHub ? (
          <SubTabs ariaLabel={t('automation.title')} items={[
            { id: 'pending', label: `${t('automation.tab_pending')}${counts.pending ? ` (${counts.pending})` : ''}`, active: tab === 'pending', onClick: () => setTab('pending') },
            { id: 'all', label: t('automation.tab_all'), active: tab === 'all', onClick: () => setTab('all') },
          ]} />
        ) : (
        <div className="a-tabs">
          <button type="button" className={`a-tab${tab === 'pending' ? ' on' : ''}`} onClick={() => setTab('pending')}>
            {t('automation.tab_pending')}{counts.pending ? ` (${counts.pending})` : ''}
          </button>
          <button type="button" className={`a-tab${tab === 'all' ? ' on' : ''}`} onClick={() => setTab('all')}>
            {t('automation.tab_all')}
          </button>
        </div>
        )}

        {isLoading ? (
          <div className="a-grid">{[0, 1, 2].map((i) => <Skeleton key={i} height="170px" borderRadius="20px" />)}</div>
        ) : suggestions.length === 0 ? (
          <div className="a-empty">
            <div className="big">🎉</div>
            <div className="h">{t('automation.all_caught')}</div>
            <div>{t('automation.all_caught_sub')}</div>
          </div>
        ) : (
          <div className="a-grid">
            {suggestions.map((s) => (
              <Card key={s.id} s={s} t={t} isAr={isAr} busy={busyId === s.id}
                onApprove={() => act(approve, s.id)} onDismiss={() => act(dismiss, s.id)} />
            ))}
          </div>
        )}

        <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} t={t} />
      </div>
    </Layout>
  );
}

function Card({ s, t, isAr, busy, onApprove, onDismiss }) {
  const params = { ...(s.params || {}), name: s.customer_name || t('automation.a_guest') };
  if (s.params?.reward) params.reward = isAr && s.params.reward_ar ? s.params.reward_ar : s.params.reward;
  const pending = s.status === 'pending';

  const approveLabel = {
    send_email: t('automation.cta_send'),
    tv_celebrate: t('automation.cta_celebrate'),
    award_points: t('automation.cta_award'),
    none: t('automation.cta_ok'),
  }[s.action_type] || t('automation.cta_ok');

  return (
    <div className={`a-card ${s.tone || 'attention'}`}>
      <div className="a-card-for">{t('automation.for_customer', { name: s.customer_name || t('automation.a_guest') })}</div>
      <div className="a-card-title">{t(`automation.card.${s.type}.title`, params)}</div>
      <div className="a-card-body">{t(`automation.card.${s.type}.body`, params)}</div>

      {pending ? (
        <div className="a-card-btns">
          <Button label={approveLabel} icon="pi pi-check" loading={busy} onClick={onApprove} />
          <Button label={t('automation.dismiss')} icon="pi pi-times" text severity="secondary" disabled={busy} onClick={onDismiss} />
        </div>
      ) : (
        <span className={`a-status ${s.status}`}>
          {t(`automation.status.${s.status}`)}{s.status === 'failed' && s.result_note ? ` · ${s.result_note}` : ''}
        </span>
      )}
    </div>
  );
}

function SettingsDialog({ open, onClose, t }) {
  const { data } = useAutomationSettings();
  const save = useUpdateAutomationSettings();
  const [modes, setModes] = useState(null);
  const [tv, setTv] = useState(false);

  // Hydrate local state when the dialog opens with fresh data.
  React.useEffect(() => {
    if (open && data) { setModes({ ...(data.modes || {}) }); setTv(!!data.tv_celebration_enabled); }
  }, [open, data]);

  const modeOptions = [
    { label: t('automation.mode_off'), value: 'off' },
    { label: t('automation.mode_suggest'), value: 'suggest' },
    { label: t('automation.mode_auto'), value: 'auto' },
  ];

  const submit = () => save.mutate({ modes, tv_celebration_enabled: tv }, { onSuccess: onClose });

  return (
    <Dialog visible={open} onHide={onClose} className="auto360-dlg" style={{ width: '34rem', maxWidth: '95vw' }}
      header={t('automation.settings_title')} modal
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label={t('automation.cancel')} text onClick={onClose} />
          <Button label={t('automation.save')} icon="pi pi-check" loading={save.isLoading} disabled={!modes} onClick={submit} />
        </div>
      }>
      <p className="text-sm" style={{ color: 'var(--ink-soft, #6e6154)', marginBottom: 8 }}>{t('automation.settings_sub')}</p>
      {modes && TRIGGERS.map((type) => (
        <div className="s-row" key={type}>
          <div className="s-label">
            {t(`automation.trigger.${type}`)}
            <div className="s-hint">{t(`automation.trigger_hint.${type}`)}</div>
          </div>
          <Dropdown value={modes[type] || 'suggest'} options={modeOptions}
            onChange={(e) => setModes((m) => ({ ...m, [type]: e.value }))} style={{ width: '10rem' }} />
        </div>
      ))}
      <div className="s-row">
        <div className="s-label">
          {t('automation.tv_label')}
          <div className="s-hint">{t('automation.tv_hint')}</div>
        </div>
        <InputSwitch checked={tv} onChange={(e) => setTv(e.value)} />
      </div>
    </Dialog>
  );
}
